import { useTheme } from "@modernatx-internal/component-library";
import React from "react";

import { useFinder } from "@/finder/hooks/V2/useFinder";
import type { FinderLocation } from "@/types/FinderLocation";

interface MarkerProps {
  location: FinderLocation;
  supportsHover: boolean;
}

export const Marker: React.FC<MarkerProps> = ({ location, supportsHover }) => {
  const theme = useTheme();
  const { locationHovered, locationHoveredSet, locationSelected } = useFinder();

  const active = locationSelected?.customer.id === location.customer.id;
  const hovered = locationHovered?.customer.id === location.customer.id;

  // Create unique filter IDs to prevent iOS Safari conflicts
  const activeFilterId = `filter0_d_active_${location.customer.id}`;
  const inactiveFilterId = `filter0_d_${location.customer.id}`;

  const handleMouseEnter = () => {
    // Only enable hover effects on devices that support true hovering
    if (supportsHover && !hovered) {
      locationHoveredSet(location);
    }
  };

  const handleMouseLeave = () => {
    // Only enable hover effects on devices that support true hovering
    if (supportsHover && hovered) {
      locationHoveredSet(null);
    }
  };

  const hoverColor = hovered
    ? (theme.colors["onSurface-brand-primary-hover"] as string)
    : (theme.colors["onSurface-brand-primary"] as string);

  const activeStrokeColor = theme.colors["color-current-brand-700"] as string;
  const activeCircleColor = theme.colors["color-current-brand-600"] as string;

  return active ? (
    <svg
      data-testid="map-marker"
      width={40}
      height={50}
      viewBox="0 0 48 58"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <g filter={`url(#${activeFilterId})`}>
        <path
          d="M44 20C44 33.75 26.5 50 24 50C21.5 50 4 33.75 4 20C4 8.9543 12.9543 0 24 0C35.0457 0 44 8.9543 44 20Z"
          fill="white"
        />
        <path
          d="M24 1C34.4934 1 43 9.50659 43 20C43 26.5128 38.819 33.7816 34.1611 39.5264C31.8522 42.3741 29.4696 44.7932 27.5225 46.4922C26.5474 47.343 25.6956 48.0004 25.0283 48.4385C24.6936 48.6582 24.4226 48.8119 24.2178 48.9062C24.1058 48.9578 24.0352 48.9805 24 48.9912C23.9648 48.9805 23.8942 48.9578 23.7822 48.9062C23.5774 48.8119 23.3064 48.6582 22.9717 48.4385C22.3044 48.0004 21.4526 47.343 20.4775 46.4922C18.5304 44.7932 16.1478 42.3741 13.8389 39.5264C9.18097 33.7816 5 26.5128 5 20C5 9.50659 13.5066 1 24 1Z"
          stroke={activeStrokeColor}
          strokeWidth="2"
        />
      </g>
      <circle cx="24" cy="20" r="7.5" fill={activeCircleColor} />
      <defs>
        <filter
          id={activeFilterId}
          x="0"
          y="0"
          width="48"
          height="58"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
      </defs>
    </svg>
  ) : (
    <svg
      data-testid="map-marker"
      width={32}
      height={40}
      viewBox="0 0 40 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <g filter={`url(#${inactiveFilterId})`}>
        <path
          data-testid="marker-path"
          d="M36 16C36 27 22 40 20 40C18 40 4 27 4 16C4 7.16344 11.1634 0 20 0C28.8366 0 36 7.16344 36 16Z"
          fill={hoverColor}
        />
        <path
          d="M20 1C28.2843 1 35 7.71573 35 16C35 21.1378 31.694 26.9066 27.9736 31.4951C26.1334 33.7647 24.2352 35.6916 22.6865 37.043C21.9106 37.72 21.2366 38.2402 20.7129 38.584C20.45 38.7566 20.2425 38.8734 20.0908 38.9434C20.054 38.9603 20.0231 38.9695 20 38.9785C19.9769 38.9695 19.946 38.9603 19.9092 38.9434C19.7575 38.8734 19.55 38.7566 19.2871 38.584C18.7634 38.2402 18.0894 37.72 17.3135 37.043C15.7648 35.6916 13.8666 33.7647 12.0264 31.4951C8.30597 26.9066 5 21.1378 5 16C5 7.71573 11.7157 1 20 1Z"
          stroke="white"
          strokeWidth="2"
        />
      </g>
      <circle cx="20" cy="16" r="6" fill="white" />
      <defs>
        <filter
          data-testid="marker-filter"
          id={inactiveFilterId}
          x="0"
          y="0"
          width="40"
          height="48"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
      </defs>
    </svg>
  );
};
